// OpenLayers layer construction for the `abrar` GeoServer workspace. The layer CATALOG itself
// (which layers exist, their titles/dates) is no longer here — it comes from GET /api/v1/layers
// (ROADMAP.md v0.6.3, MapContext.tsx), replacing this file's old hardcoded DATED_RASTERS/
// STATIC_LAYERS constants. What's left is purely mechanical: given a name/title/date, build the
// actual OL tile layer that fetches from GeoServer directly (tiles never go through the API,
// ADR-0001) — RasterLayerDef/VectorLayerDef are still the shape MapContext builds from an API
// response before calling these.
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import OSM from 'ol/source/OSM';
import { createXYZ } from 'ol/tilegrid';
import { GEOSERVER_WORKSPACE, WMS_URL } from '../config/env';

export interface RasterLayerDef {
  /** GeoServer layer name, without the workspace prefix. */
  name: string;
  /** Human label for the Scene Manager tree. */
  title: string;
  /** ISO date this product represents, for the Timeline bar. */
  date: string;
  product: 'orthomosaic' | 'dsm' | 'dtm';
}

export interface VectorLayerDef {
  name: string;
  title: string;
}

export function wmsSource(layerName: string): TileWMS {
  return new TileWMS({
    url: WMS_URL,
    params: { LAYERS: `${GEOSERVER_WORKSPACE}:${layerName}`, TILED: true },
    serverType: 'geoserver',
    // 512px tiles requested directly (sharp on retina) with NO hidpi: OpenLayers' own hidpi mode
    // doubles a 256px tile and adds FORMAT_OPTIONS=dpi:N, a parameter GeoServer's tile cache
    // (GeoWebCache) has no filter for -- every such request was a cache MISS, re-rendering the
    // satellite COG from R2 (~0.8s a tile) on every pan. Plain 512px tiles line up with the
    // WebMercatorQuadx2 gridset, so repeat views are served from the cache (~0.2s).
    hidpi: false,
    tileGrid: createXYZ({ tileSize: 512 }),
    // Never let an unreachable GeoServer during local dev/build hang or
    // crash the app — tile load errors are swallowed at the layer/UI level
    // (see MapView.tsx's tileloaderror handling), not here.
    transition: 0,
  });
}

export function makeRasterLayer(def: RasterLayerDef, opts: { visible?: boolean; opacity?: number } = {}) {
  const layer = new TileLayer({
    source: wmsSource(def.name),
    visible: opts.visible ?? false,
    opacity: opts.opacity ?? 1,
    properties: { id: `raster:${def.name}`, title: def.title, date: def.date, kind: 'raster', def },
  });
  return layer;
}

export function makeVectorTileLayer(def: VectorLayerDef, opts: { visible?: boolean; opacity?: number } = {}) {
  const layer = new TileLayer({
    source: wmsSource(def.name),
    visible: opts.visible ?? true,
    opacity: opts.opacity ?? 1,
    properties: { id: `vector:${def.name}`, title: def.title, kind: 'vector', def },
  });
  return layer;
}

/** Plain OSM basemap — this platform has no first-party basemap tile
 * service of its own; only the `abrar` workspace layers come from
 * GeoServer. */
export function makeBasemapLayer() {
  return new TileLayer({
    source: new OSM(),
    properties: { id: 'basemap:osm', title: 'Basemap', kind: 'basemap' },
  });
}
